import { useCallback, useEffect, useMemo, useRef } from "react";

// 카운트다운 커스텀 훅
// - 1초 tick 횟수를 세지 않고 "실행 예정 시각(deadline)"을 기준으로 동작한다.
//   렌더링 지연·백그라운드 탭 스로틀링으로 타이머가 늦게 실행되어도 주기가 늘어나지 않는다.
// - 남은 시간을 state로 들고 있지 않는다. (매초 setState 시 이 훅을 쓰는 페이지 전체가 리렌더되어 부하가 컸음)
//   화면 표시가 필요하면 getRemaining()을 읽는다. (예: CountdownText)
const useCountdown = (initialValue: number, callbackFunction: () => void) => {
  const durationMs = initialValue * 1000;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const deadlineRef = useRef<number | null>(null); // 콜백 실행 예정 시각 (정지 상태면 null)
  const pausedRemainingRef = useRef<number | null>(null); // 일시 정지 시점의 남은 시간(ms)
  const callbackRef = useRef<() => void>(callbackFunction);

  // 콜백 업데이트
  useEffect(() => {
    callbackRef.current = callbackFunction;
  }, [callbackFunction]);

  const clear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    deadlineRef.current = null;
  }, []);

  // delay(ms) 뒤 콜백 실행. 실행 후 다음 주기는 실제 실행 시각 기준으로 다시 잡는다.
  const schedule = useCallback(
    (delay: number) => {
      clear();
      pausedRemainingRef.current = null;
      deadlineRef.current = Date.now() + delay;

      timeoutRef.current = setTimeout(function run() {
        deadlineRef.current = Date.now() + durationMs;
        timeoutRef.current = setTimeout(run, durationMs);
        // 다음 주기를 먼저 잡고 콜백을 실행해야 콜백 안에서 stop()/reset()을 호출했을 때 그대로 반영된다
        callbackRef.current?.();
      }, delay);
    },
    [clear, durationMs],
  );

  // 카운트다운 시작
  const start = useCallback(() => schedule(durationMs), [schedule, durationMs]);

  // 카운트다운 일시 정지
  const pause = useCallback(() => {
    if (deadlineRef.current === null) return;
    const remaining = Math.max(0, deadlineRef.current - Date.now());
    clear();
    pausedRemainingRef.current = remaining;
  }, [clear]);

  // 카운트다운 재개
  const resume = useCallback(() => {
    if (pausedRemainingRef.current === null) return;
    schedule(pausedRemainingRef.current);
  }, [schedule]);

  // 카운트다운 중지
  const stop = useCallback(() => {
    clear();
    pausedRemainingRef.current = null;
  }, [clear]);

  // 카운트다운 재설정
  const reset = useCallback(() => {
    stop();
    start();
  }, [stop, start]);

  // 남은 시간(초)
  const getRemaining = useCallback(() => {
    if (deadlineRef.current !== null) return Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
    if (pausedRemainingRef.current !== null) return Math.ceil(pausedRemainingRef.current / 1000);
    return initialValue;
  }, [initialValue]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => clear, [clear]);

  // 반환 객체의 identity 안정화: 소비처의 useEffect/useCallback deps에 넣어도 재실행되지 않도록 한다
  return useMemo(() => ({ start, stop, reset, pause, resume, getRemaining }), [start, stop, reset, pause, resume, getRemaining]);
};

export default useCountdown;
